import { useEffect, useMemo, useState } from "react";
import { Undo2, FileSpreadsheet, CheckCircle2, XCircle, Loader2, Download, ArrowRight, ArrowLeft, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useWorkspace } from "../context/WorkspaceContext";
import { SearchableSelect } from "../components/common/SearchableSelect";
import { PageHeader } from "../components/common/PageHeader";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const CAT_STYLE = {
  VALID: "bg-emerald-100 text-emerald-700",
  INVALID_SERIAL: "bg-red-100 text-red-700",
  UNKNOWN_TYPE: "bg-red-100 text-red-700",
  NOT_FOUND: "bg-red-100 text-red-700",
  INELIGIBLE_INSTALLED: "bg-red-100 text-red-700",
  ALREADY_SENT: "bg-red-100 text-red-700",
  INELIGIBLE_STATUS: "bg-red-100 text-red-700",
  DUPLICATE_IN_FILE: "bg-red-100 text-red-700",
};
const CAT_LABELS = {
  VALID: "Valid (can send back)",
  INVALID_SERIAL: "Blank material number",
  UNKNOWN_TYPE: "Unknown material type",
  NOT_FOUND: "Not found in project",
  INELIGIBLE_INSTALLED: "Installed / Completed (blocked)",
  ALREADY_SENT: "Already sent / accepted",
  INELIGIBLE_STATUS: "Ineligible status",
  DUPLICATE_IN_FILE: "Duplicate in file",
};

export default function MaterialSendBack() {
  const { ctx, options } = useWorkspace();
  const [projectId, setProjectId] = useState(ctx.project?.id || "");
  const [confirmed, setConfirmed] = useState(false);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [dlBusy, setDlBusy] = useState(false);
  const [catFilter, setCatFilter] = useState("__all__");

  useEffect(() => { setProjectId(ctx.project?.id || ""); setConfirmed(false); }, [ctx.project?.id]);
  const projects = useMemo(() => options.projects || [], [options.projects]);
  const projName = useMemo(() => projects.find((p) => p.id === projectId), [projects, projectId]);
  const changeProject = (v) => { setProjectId(v); setConfirmed(false); setPreview(null); };

  const downloadTemplate = async () => {
    setDlBusy(true);
    try {
      const res = await api.get("/materials/sendback/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = "material_sendback_template.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Template download failed"); }
    finally { setDlBusy(false); }
  };

  const doPreview = async () => {
    if (!projectId || !file) { toast.error("Confirm project and choose a file"); return; }
    setLoading(true); setPreview(null);
    try {
      const fd = new FormData(); fd.append("file", file); fd.append("project_id", projectId);
      const { data } = await api.post("/materials/sendback/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (data.already_applied) toast.info("This exact file was already applied for this project");
    } catch (e) { toast.error(e.response?.data?.detail || "Preview failed"); }
    finally { setLoading(false); }
  };

  const doApply = async () => {
    if (!preview?.batch_id) return;
    setApplying(true);
    try {
      const { data } = await api.post("/materials/sendback/apply", { batch_id: preview.batch_id });
      toast.success(`Sent back: ${data.sent_back}${data.failed ? `, ${data.failed} failed` : ""} — now in Client Returns for approval`);
      setPreview((p) => ({ ...p, already_applied: true, result: data }));
    } catch (e) { toast.error(e.response?.data?.detail || "Apply failed"); }
    finally { setApplying(false); }
  };

  const reset = () => { setFile(null); setPreview(null); setCatFilter("__all__"); };

  const downloadReport = async () => {
    if (!preview?.batch_id) return;
    try {
      const res = await api.get(`/materials/sendback/${preview.batch_id}/report`, {
        params: catFilter !== "__all__" ? { category: catFilter } : {}, responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `material_sendback_validation_${preview.batch_id.slice(0, 8)}.xlsx`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error("Report download failed"); }
  };

  const rows = useMemo(() => {
    if (!preview?.rows) return [];
    return catFilter === "__all__" ? preview.rows : preview.rows.filter((r) => r.category === catFilter);
  }, [preview, catFilter]);

  const s = preview?.summary || {};
  const canApply = preview && !preview.already_applied && (s.applicable || 0) > 0;

  return (
    <div>
      <PageHeader title="Send Materials Back to Client" subtitle="End-of-project close-out. Any material NOT Installed/Completed can be sent back in bulk; it then appears in Client Returns for approval." />

      {!confirmed ? (
        <div className="max-w-2xl rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="sendback-context-step">
          <div className="mb-4 flex items-center gap-2 text-sm font-semibold text-slate-700"><ShieldCheck className="h-4 w-4 text-amber-600" /> Confirm Project</div>
          <p className="mb-4 text-xs text-slate-500">Materials listed in your file (that are currently in this project and not Installed/Completed) will be marked <b>Sent Back to Client</b> and appear in the Client Returns worklist for an authorized user to accept.</p>
          <div className="grid gap-4 md:grid-cols-2">
            <div>
              <Label>Project *</Label>
              <SearchableSelect testid="sendback-project-select" value={projectId} onChange={changeProject} options={projects.map((p) => ({ value: p.id, label: p.project_code ? `${p.project_code} — ${p.project_name}` : p.project_name, sub: p.client || p.location }))} placeholder="Select project" className="mt-1" />
            </div>
          </div>
          <div className="mt-5 flex justify-end">
            <Button data-testid="sendback-confirm-context-btn" disabled={!projectId} onClick={() => setConfirmed(true)} className="bg-amber-600 hover:bg-amber-700">
              Confirm &amp; Continue <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <>
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-2.5" data-testid="sendback-confirmed-bar">
            <div className="text-sm text-slate-700"><span className="text-slate-400">Project:</span> <span className="font-semibold text-amber-800">{projName?.project_name}</span></div>
            <Button variant="outline" size="sm" data-testid="sendback-back-context-btn" onClick={() => { setConfirmed(false); reset(); }}><ArrowLeft className="mr-1.5 h-4 w-4" /> Change Project</Button>
          </div>

          <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
              <span className="text-sm font-medium text-slate-600">Upload the template (Material Type · Material Number · Reason)</span>
              <Button variant="outline" size="sm" data-testid="sendback-template-download" onClick={downloadTemplate} disabled={dlBusy}>
                {dlBusy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Download className="mr-1.5 h-4 w-4" />} Download Template
              </Button>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <label className="flex flex-1 cursor-pointer items-center gap-3 rounded-lg border border-dashed border-slate-300 px-4 py-3 hover:bg-slate-50" data-testid="sendback-file-label">
                <FileSpreadsheet className="h-5 w-5 text-amber-600" />
                <span className="text-sm text-slate-600">{file ? file.name : "Choose .xlsx file"}</span>
                <input type="file" accept=".xlsx,.xls" className="hidden" data-testid="sendback-file-input" onChange={(e) => { setFile(e.target.files[0]); setPreview(null); }} />
              </label>
              <Button data-testid="sendback-preview-btn" onClick={doPreview} disabled={loading || !file} className="bg-slate-800 hover:bg-slate-900">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Undo2 className="mr-1.5 h-4 w-4" /> Preview</>}
              </Button>
            </div>
          </div>

          {preview && (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4" data-testid="sendback-summary">
                <SummaryCard label="Total" value={s.total} tone="slate" />
                <SummaryCard label="Valid" value={s.valid} tone="green" icon={CheckCircle2} />
                <SummaryCard label="Errors" value={s.error} tone="red" icon={XCircle} />
                <SummaryCard label="Applicable" value={s.applicable} tone="amber" />
              </div>

              {Object.keys(preview.categories || {}).length > 0 && (
                <div className="rounded-lg border border-slate-200 bg-white p-3 shadow-sm" data-testid="sendback-cat-breakdown">
                  <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Breakdown by result</div>
                  <div className="flex flex-wrap gap-2">
                    {Object.entries(preview.categories).sort((a, b) => (a[0] === "VALID" ? 1 : 0) - (b[0] === "VALID" ? 1 : 0) || b[1] - a[1]).map(([cat, count]) => (
                      <button key={cat} type="button" onClick={() => setCatFilter((prev) => (prev === cat ? "__all__" : cat))}
                        data-testid={`sendback-cat-chip-${cat}`}
                        className={`rounded-full border px-3 py-1 text-xs transition ${CAT_STYLE[cat] || "bg-slate-100 text-slate-600"} ${catFilter === cat ? "ring-2 ring-offset-1 ring-slate-400" : "hover:opacity-90"}`}>
                        <span className="font-bold">{count}</span> × {CAT_LABELS[cat] || cat}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex flex-wrap items-center justify-between gap-3">
                <Select value={catFilter} onValueChange={setCatFilter}>
                  <SelectTrigger data-testid="sendback-cat-filter" className="w-64"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All categories</SelectItem>
                    {Object.keys(preview.categories || {}).map((c) => <SelectItem key={c} value={c}>{CAT_LABELS[c] || c} ({preview.categories[c]})</SelectItem>)}
                  </SelectContent>
                </Select>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={downloadReport} data-testid="sendback-download-report"><Download className="mr-1.5 h-4 w-4" /> Download Report</Button>
                  {preview.already_applied ? (
                    <Badge className="bg-emerald-100 px-3 py-1.5 text-emerald-700">Sent back · {preview.result?.sent_back ?? 0}</Badge>
                  ) : (
                    <>
                      <Button variant="outline" onClick={reset} data-testid="sendback-reset-btn">Reset</Button>
                      <Button data-testid="sendback-apply-btn" onClick={doApply} disabled={!canApply || applying} className="bg-amber-600 hover:bg-amber-700">
                        {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : `Send ${s.applicable || 0} back to client`}
                      </Button>
                    </>
                  )}
                </div>
              </div>

              <div className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm">
                <table className="w-full text-left">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-100/80">
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Row</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Material No.</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Type</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Current Status</th>
                      <th className="px-4 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">Result</th>
                    </tr>
                  </thead>
                  <tbody data-testid="sendback-rows">
                    {rows.slice(0, 500).map((r) => (
                      <tr key={r.row_no} className="border-b border-slate-100">
                        <td className="px-4 py-2 text-sm text-slate-500">{r.row_no}</td>
                        <td className="px-4 py-2 font-mono text-xs text-slate-800">{r.serial || <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{r.material_type}</td>
                        <td className="px-4 py-2 text-sm text-slate-600">{(r.status || "").replace(/_/g, " ") || <span className="text-slate-300">—</span>}</td>
                        <td className="px-4 py-2">
                          <Badge className={`${CAT_STYLE[r.category] || "bg-slate-100 text-slate-600"}`}>{CAT_LABELS[r.category] || r.category}</Badge>
                          {r.message && <span className="ml-2 text-xs text-slate-400">{r.message}</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function SummaryCard({ label, value, tone, icon: Icon }) {
  const tones = { slate: "text-slate-700", green: "text-emerald-600", red: "text-red-600", amber: "text-amber-600" };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">{Icon && <Icon className="h-3.5 w-3.5" />}{label}</div>
      <div className={`mt-1 font-display text-2xl font-bold ${tones[tone]}`}>{value ?? 0}</div>
    </div>
  );
}
